import { supabase } from '../supabase';

export interface NotificationRow {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  module: 'hr' | 'sales' | 'finance' | 'system';
  is_read: boolean;
  action_url: string | null;
  created_at: string;
}

export interface SendNotificationOptions {
  user_ids: string[];
  title: string;
  message: string;
  type?: NotificationRow['type'];
  module?: NotificationRow['module'];
  action_url?: string;
}

export async function getNotifications(profileId: string): Promise<NotificationRow[]> {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', profileId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw new Error(error.message);
  return (data ?? []) as NotificationRow[];
}

export async function getUnreadCount(profileId: string): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', profileId)
    .eq('is_read', false);
  if (error) throw new Error(error.message);
  return count ?? 0;
}

export async function markAsRead(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId);
  if (error) throw new Error(error.message);
}

export async function markAllAsRead(profileId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('user_id', profileId)
    .eq('is_read', false);
  if (error) throw new Error(error.message);
}

export async function sendNotification(opts: SendNotificationOptions): Promise<void> {
  // Use supabase.functions.invoke which automatically attaches the user's JWT
  const { error } = await supabase.functions.invoke('send-notification', {
    body: opts,
  });
  if (error) {
    throw new Error(`Notification send failed: ${error.message}`);
  }
}

export async function getProfilesByRole(roles: string[]): Promise<{ id: string; full_name: string }[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name')
    .in('role', roles)
    .eq('account_status', 'approved');
  if (error) return [];
  return data ?? [];
}

export async function getProfileById(profileId: string): Promise<{ id: string; full_name: string } | null> {
  const { data } = await supabase
    .from('profiles')
    .select('id, full_name')
    .eq('id', profileId)
    .maybeSingle();
  return data;
}

export async function sendNotificationsToRoles(opts: {
  roles: string[];
  title: string;
  message: string;
  type?: NotificationRow['type'];
  module?: NotificationRow['module'];
  action_url?: string;
}): Promise<void> {
  const users = await getProfilesByRole(opts.roles);
  if (!users.length) return;
  await sendNotification({
    user_ids: users.map(u => u.id),
    title: opts.title,
    message: opts.message,
    type: opts.type,
    module: opts.module,
    action_url: opts.action_url,
  });
}

export async function sendNotificationToUser(opts: {
  user_id: string;
  title: string;
  message: string;
  type?: NotificationRow['type'];
  module?: NotificationRow['module'];
  action_url?: string;
}): Promise<void> {
  await sendNotification({
    user_ids: [opts.user_id],
    title: opts.title,
    message: opts.message,
    type: opts.type,
    module: opts.module,
    action_url: opts.action_url,
  });
}
